import React from 'react';
import { v4 as uuidv4 } from 'uuid';
import { UpdateRouteMapProps } from './types';
import { useGetOrderMissionQuery } from '@src/features/order/redux';
import { useParams, useSearchParams } from 'react-router-dom';
import { MissionIdParams } from '@src/core/entities';
import { useDispatch, useSelector } from 'react-redux';
import { selectDrone, setWaypoints } from '@src/features/order/pages/upsert-mission-page/redux';
import { RouteWaypointEntity, RouteWaypointType } from '@src/features/order/pages/upsert-mission-page/entities';
import { RouteWPS } from '@src/features/order/pages/upsert-mission-page/components';
import { splitHomeLeg } from '@src/features/order/pages/upsert-mission-page/utils';
import DroneHomePath from '@src/features/order/pages/upsert-mission-page/components/drone-home-path';
import { DroneStatusType, useDroneProvider } from '@src/core/features';
import { ArcDroneMarker, TakeOffMarker } from '@src/core/controllers';
import { arcConvertBetweenUnitTypes, arcToFixed } from '@src/core/extensions';
import { MeasurementType } from '@src/core/features/settings/entities';
import { UnitType } from '@src/core/enums';
import { getUnitType } from '@src/core/features/settings/units';

// REST waypoint altitudes are SI (m); editable form inputs use the user's display unit.
const altitudeSiToDisplay = (value: number | string | undefined | null): string => {
  const num = Number(value);
  if (!Number.isFinite(num)) return '';
  const converted = arcConvertBetweenUnitTypes(num, MeasurementType.DISTANCE, UnitType.SI, getUnitType());
  return arcToFixed(converted, 1);
};

const UpdateRouteMap: React.FC<UpdateRouteMapProps> = () => {
  const dispatch = useDispatch();
  const params = useParams<MissionIdParams>();
  const [searchParams] = useSearchParams();
  const { data, isLoading } = useGetOrderMissionQuery(params.missionId);
  const { drones: idleDrones } = useDroneProvider(DroneStatusType.IDLE);
  const selectedDrone = useSelector(selectDrone);

  const droneSerial = searchParams.get('droneSerial');
  const droneForLine =
    selectedDrone ??
    idleDrones.find((d) => d.serialNumber === droneSerial) ??
    data?.drone ??
    null;

  const routeWaypoints = React.useMemo(
    () => splitHomeLeg(data?.waypoints ?? []).routeWaypoints,
    [data],
  );

  const takeoff = routeWaypoints.find((w) => !w.orderId) ?? routeWaypoints[0];

  React.useEffect(() => {
    if (!data) return;
    const size = routeWaypoints.length;
    dispatch(setWaypoints(routeWaypoints.map((w, idx) => {
      const updatedWp: RouteWaypointEntity = {
        uuid: uuidv4(),
        orderId: w.orderId,
        latitude: w.latitude,
        longitude: w.longitude,
        altitude: altitudeSiToDisplay(w.altitude),
        type: RouteWaypointType.WAYPOINT,
      };

      if (w.orderId) {
        return {
          ...updatedWp,
          type: RouteWaypointType.DESTINATION,
        };
      }

      if (idx === 0 || idx === size -1) {
        return {
          ...updatedWp,
          type: RouteWaypointType.TAKE_OFF,
        };
      }

      return updatedWp;
    })));
  }, [data, routeWaypoints, dispatch]);

  if (isLoading) return null;

  return (
    <React.Fragment>
      {idleDrones.map((drone) =>
        drone.id === selectedDrone?.id ? (
          <ArcDroneMarker.idleSelected
            key={drone.id}
            position={{
              lat: Number(drone.latitude),
              lng: Number(drone.longitude),
            }}
          />
        ) : (
          <ArcDroneMarker.idleArrow
            key={drone.id}
            position={{
              lat: Number(drone.latitude),
              lng: Number(drone.longitude),
            }}
          />
        ),
      )}

      {/* The assigned drone is usually no longer IDLE, so it's missing from idleDrones above.
          Render it explicitly so the selected-drone marker shows at the green line's origin. */}
      {droneForLine && !idleDrones.some((d) => d.id === droneForLine.id) && (
        <ArcDroneMarker.idleSelected
          position={{ lat: Number(droneForLine.latitude), lng: Number(droneForLine.longitude) }}
        />
      )}

      {takeoff && (
        <TakeOffMarker.confirmed
          position={{ lat: Number(takeoff.latitude), lng: Number(takeoff.longitude) }}
        />
      )}

      {droneForLine && takeoff && (
        <DroneHomePath
          from={{ lat: Number(droneForLine.latitude), lng: Number(droneForLine.longitude) }}
          to={{ lat: Number(takeoff.latitude), lng: Number(takeoff.longitude) }}
        />
      )}

      <RouteWPS takeoffDrone='hidden' />
    </React.Fragment>
  );
};

export default UpdateRouteMap;